import type { CSSProperties } from "react";
import type { DialDocument, DocumentCategory, DocumentTag } from "@keydials/api-client";

// The Files pane's upload tags: what the picker offers, how the file list groups, and what a half-typed tag
// name means at the moment somebody uploads. Pure, so the rules are pinned without rendering a panel.

export type CategoryOption = { id: DocumentCategory; label: string; hint: string };

/** Files already filed under the old built-in phases group first, in this order. KEY-644 took them out of
 * the picker — a dial that wants them adds them like any other tag — but evidence filed under them before
 * that still leads the list, so nobody's files move. */
const PHASE_ORDER: readonly string[] = ["Found", "Resolution"];

/** The untagged bucket. Always LAST, whatever a dial has added before it: it is the catch-all, and a
 * catch-all in the middle of a row of specific tags reads as one of them. */
export const OTHER_CATEGORY: CategoryOption = {
  id: "Uncategorized",
  label: "Other",
  hint: "Anything you haven't given a tag of its own",
};

/** The picker's pills for one dial: the dial's own tags in creation order (KEY-499), then "Other".
 *
 * Found/Resolution used to lead every dial's row (KEY-644). They were a product guess at a vocabulary most
 * dials never used, and they crowded out the tags people actually add. */
export function categoryOptions(tags: readonly DocumentTag[]): CategoryOption[] {
  return [
    ...tags.map((t) => ({ id: t.label, label: t.label, hint: "A tag this dial added" })),
    OTHER_CATEGORY,
  ];
}

/**
 * The file list's groups, derived from the DOCUMENTS rather than by mapping over a fixed list of tags.
 *
 * That direction matters (KEY-499). A document's `category` is a denormalised label with no foreign key, so
 * it is NOT guaranteed to name a tag that still exists — a dial can withdraw a tag, and the files filed
 * under it deliberately keep it. Filtering a fixed list of options would silently drop those files from the
 * list they are supposed to be in, which is the worst possible failure for evidence.
 *
 * Order: Found, Resolution, the dial's own tags in creation order, then anything left — a withdrawn tag,
 * still under its own name rather than folded into "Other" — and "Other" last.
 */
export function documentGroups(
  docs: readonly DialDocument[],
  tags: readonly DocumentTag[],
): { id: string; label: string; docs: DialDocument[] }[] {
  const byCategory = new Map<string, DialDocument[]>();
  for (const d of docs) {
    const key = d.category || OTHER_CATEGORY.id;
    const bucket = byCategory.get(key);
    if (bucket) {
      bucket.push(d);
    } else {
      byCategory.set(key, [d]);
    }
  }

  const groups: { id: string; label: string; docs: DialDocument[] }[] = [];
  const take = (id: string) => {
    const found = byCategory.get(id);
    if (found) {
      groups.push({ id, label: id, docs: found });
      byCategory.delete(id);
    }
  };

  for (const phase of PHASE_ORDER) {
    take(phase);
  }
  for (const tag of tags) {
    take(tag.label);
  }

  // "Other" is pulled out before the leftovers so it stays last even when a withdrawn tag is in play.
  const untagged = byCategory.get(OTHER_CATEGORY.id);
  byCategory.delete(OTHER_CATEGORY.id);
  for (const [id, group] of byCategory) {
    groups.push({ id, label: id, docs: group });
  }
  if (untagged) {
    groups.push({ id: OTHER_CATEGORY.id, label: OTHER_CATEGORY.label, docs: untagged });
  }
  return groups;
}

/** Trims and collapses internal whitespace — the server's `NormaliseLabel`, so the client matches a typed
 * name against the dial's tags exactly as the server will when it files the upload. */
export function normaliseTagLabel(label: string): string {
  return label.split(/\s+/).filter(Boolean).join(" ");
}

/** What a half-typed tag name means at upload time. */
export type PendingTag =
  /** Nothing typed: the selected pill stands. */
  | { kind: "none" }
  /** Names a tag the dial already has (or "Other"): file under its stored spelling, create nothing. */
  | { kind: "existing"; category: DocumentCategory }
  /** A new name: the tag must be added before the upload is filed under it. */
  | { kind: "create"; label: string };

/**
 * Resolves the "+ Add a tag" input's draft against the dial's tags (KEY-644).
 *
 * The bug this exists for: the draft lived only in the picker, so typing "Policy" and uploading WITHOUT
 * pressing Add filed the file as Other — the name on screen was silently discarded. Now the upload asks
 * this first. Matching is case- and whitespace-insensitive like the server's, so "  policy " finds an
 * existing "Policy" rather than trying to add a duplicate the server would refuse.
 *
 * A new name that spells one of the old built-ins comes back in the built-in's casing ("found" → "Found"):
 * the server stores it that way and files uploads under it that way, so the client has to agree on which
 * pill is selected.
 */
export function resolvePendingTag(draft: string, tags: readonly DocumentTag[]): PendingTag {
  const label = normaliseTagLabel(draft);
  if (label.length === 0) {
    return { kind: "none" };
  }
  const key = label.toLowerCase();
  if (key === "other" || key === OTHER_CATEGORY.id.toLowerCase()) {
    return { kind: "existing", category: OTHER_CATEGORY.id };
  }
  const match = tags.find((t) => normaliseTagLabel(t.label).toLowerCase() === key);
  if (match) {
    return { kind: "existing", category: match.label };
  }
  const builtIn = PHASE_ORDER.find((p) => p.toLowerCase() === key);
  return { kind: "create", label: builtIn ?? label };
}

/**
 * One picker pill. The selected one is a solid accent fill with the on-accent ink (KEY-644): the old
 * `--accent-soft` wash with `--accent-text` sat too close to the unselected pills to read as "this one",
 * worst in the dark theme. Both tokens are defined by every host that renders the workspace.
 */
export function tagPillStyle(on: boolean): CSSProperties {
  return {
    font: "inherit",
    fontSize: 11.5,
    fontWeight: 700,
    padding: "4px 10px",
    borderRadius: 99,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    border: `var(--border-w) solid ${on ? "var(--accent)" : "var(--border)"}`,
    background: on ? "var(--accent)" : "var(--surface)",
    color: on ? "var(--on-accent)" : "var(--text-muted)",
  };
}
